import React, { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";

const PASSWORD_RULES = [
    { test: (p) => p.length >= 8, label: "Mínimo 8 caracteres" },
    { test: (p) => /[a-z]/.test(p), label: "Una letra minúscula" },
    { test: (p) => /[A-Z]/.test(p), label: "Una letra mayúscula" },
    { test: (p) => /[0-9]/.test(p), label: "Un número" },
    { test: (p) => /[^a-zA-Z0-9]/.test(p), label: "Un carácter especial (!@#$%...)" },
];

const isPasswordValid = (password) => PASSWORD_RULES.every((r) => r.test(password));

const PasswordStrength = ({ password }) => {
    if (!password) return null;
    return (
        <ul className="mt-2 space-y-1">
            {PASSWORD_RULES.map((rule, i) => {
                const passes = rule.test(password);
                return (
                    <li key={i} className={`text-xs flex items-center gap-1 ${passes ? "text-green-600" : "text-gray-400"}`}>
                        {passes ? (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        ) : (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth={2} /></svg>
                        )}
                        {rule.label}
                    </li>
                );
            })}
        </ul>
    );
};

const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const inputClassName = "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!isPasswordValid(password)) {
            setError("La contraseña no cumple todos los requisitos");
            return;
        }

        if (password !== confirmPassword) {
            setError("Las contraseñas no coinciden");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/auth/reset-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token, password }),
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Error al restablecer la contraseña");

            setSuccess(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="container mx-auto px-4 py-16">
                <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mb-4">
                        <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Enlace inválido</h2>
                    <p className="text-gray-500 mb-6">El enlace de restablecimiento no es válido.</p>
                    <Link to="/login" className="text-blue-600 hover:underline">Ir a iniciar sesión</Link>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="container mx-auto px-4 py-16">
                <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-4">
                        <svg className="w-7 h-7 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Contraseña restablecida</h2>
                    <p className="text-gray-500 mb-6">Tu contraseña ha sido cambiada correctamente.</p>
                    <Link to="/login" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md">
                        Iniciar sesión
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-12">
            <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Nueva contraseña</h2>
                <p className="text-gray-500 text-sm mb-6">Introduce tu nueva contraseña.</p>

                {error && <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4 text-sm">{error}</div>}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nueva contraseña</label>
                        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputClassName} placeholder="Crea una contraseña segura" />
                        <PasswordStrength password={password} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar contraseña</label>
                        <input type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={inputClassName} />
                    </div>
                    <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-md shadow-sm disabled:opacity-50">
                        {loading ? "Restableciendo..." : "Restablecer contraseña"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
